import { useState } from 'react';
import { toast } from '@drbell/ui';
import { api, ApiError, type ImportPlan, type ImportResult, type OrgSummary } from '../api.ts';

/**
 * Admitting a whole home at once — RX-ORG-05.
 *
 * A care home arriving at DrBell has forty residents already, in a
 * spreadsheet, and typing them in one at a time is the reason they would
 * never start. The endpoint has existed for weeks with no way to reach it.
 *
 * The shape of this screen is the dry run. The API defaults `dryRun` to true
 * — committing has to be the deliberate call rather than the typo — and this
 * makes that visible: paste, see exactly what would happen line by line, and
 * only then import. Nobody should discover what a file did by looking at the
 * residents list afterwards.
 */
export function Import({ org, onImported }: {
  org: OrgSummary;
  onImported: () => void;
}) {
  const [csv, setCsv] = useState('');
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<ImportResult>(`/api/orgs/${org.id}/patients/import`, {
        csv,
        dryRun,
      });
      setPlan(r.plan);
      if (!dryRun) {
        toast.success(
          `${r.imported} resident${r.imported === 1 ? '' : 's'} admitted`,
          // The verb has to agree too: pluralising the noun and leaving
          // "were" gives "1 line were skipped".
          r.plan.counts.failed > 0
            ? r.plan.counts.failed === 1
              ? '1 line was skipped.'
              : `${r.plan.counts.failed} lines were skipped.`
            : undefined,
        );
        setCsv('');
        setPlan(null);
        onImported();
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    setPlan(null);
    setCsv(await file.text());
  };

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Admit several residents at once</h2>
        <p className="mt-1 max-w-2xl text-sm text-(--muted)">
          A spreadsheet of names, rooms and languages. Nothing is created until
          you have seen exactly what it would do.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="inline-flex min-h-11 cursor-pointer items-center rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)">
          Choose a CSV file
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => void readFile(e.target.files?.[0])}
          />
        </label>
        {/*
          A plain link, not a fetch. The template is served with a
          Content-Disposition header, so the browser saves it — and it is the
          one thing on this screen that works before anything has been typed.
        */}
        <a
          href={`/api/orgs/${org.id}/patients/import/template`}
          className="inline-flex min-h-11 items-center rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent)"
        >
          Download the template
        </a>
      </div>

      <label className="block">
        <span className="text-xs text-(--muted)">…or paste the rows here</span>
        <textarea
          value={csv}
          onChange={(e) => { setCsv(e.target.value); setPlan(null); }}
          rows={8}
          placeholder={'name,room,date of birth,language\nLakshmi Devi,2A,1940-03-12,Telugu'}
          className="mt-1 w-full rounded-lg border border-(--line) bg-(--panel) p-3 font-mono text-xs"
        />
        <span className="mt-1 block text-xs text-(--muted)">
          Column names are matched generously — <code>name</code>,{' '}
          <code>resident name</code> and <code>Full Name</code> all work, and a
          Language column may say “Telugu” rather than a code.
        </span>
      </label>

      {error && (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          disabled={busy || csv.trim().length === 0}
          onClick={() => void send(true)}
          className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent) disabled:opacity-40"
        >
          {busy ? 'Checking…' : 'Check the file'}
        </button>
        <button
          disabled={busy || !plan || plan.counts.ready === 0}
          title={!plan ? 'Check the file first' : undefined}
          onClick={() => void send(false)}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {plan
            ? `Admit ${plan.counts.ready} resident${plan.counts.ready === 1 ? '' : 's'}`
            : 'Admit'}
        </button>
      </div>

      {plan && <Plan plan={plan} />}
    </section>
  );
}

function Plan({ plan }: { plan: ImportPlan }) {
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 rounded-xl border border-(--line) bg-(--panel) p-4 sm:grid-cols-4">
        <Count label="Lines read" value={plan.counts.total} />
        <Count label="Will be admitted" value={plan.counts.ready} good={plan.counts.ready > 0} />
        <Count label="Will be skipped" value={plan.counts.failed} bad={plan.counts.failed > 0} />
        <Count label="Worth a look" value={plan.counts.warned} warn={plan.counts.warned > 0} />
      </dl>

      {/*
        Named, not counted. "3 columns were ignored" leaves somebody hunting;
        naming them shows at once that "Room No." was spelled in a way nothing
        recognised, and that every room is therefore about to be blank.
      */}
      {plan.unmappedColumns.length > 0 && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
          {/* No trailing full stop: a column called "Room No." made it
              "Room No..". Column names come from somebody's spreadsheet and
              can end in anything. */}
          <span>Nothing was read from these columns: </span>
          {plan.unmappedColumns.map((c, i) => (
            <span key={c}>
              {i > 0 && ', '}
              <code>{c}</code>
            </span>
          ))}
          <span>
            {' '}&mdash; if one of them holds something you need, rename it and
            check again.
          </span>
        </p>
      )}

      <div className="overflow-x-auto rounded-xl border border-(--line)">
        <table className="w-full text-sm">
          <thead className="bg-(--panel) text-left text-xs text-(--muted)">
            <tr>
              <th className="p-3 font-medium">Line</th>
              <th className="p-3 font-medium">Resident</th>
              <th className="p-3 font-medium">What will happen</th>
            </tr>
          </thead>
          <tbody>
            {plan.rows.map((row) => (
              <tr key={row.line} className="border-t border-(--line)">
                <td className="p-3 tabular-nums text-(--muted)">{row.line}</td>
                <td className="p-3">
                  {row.patient ? (
                    <>
                      <span className="font-medium">{row.patient.displayName}</span>
                      {row.patient.locationNote && (
                        <span className="ml-2 text-(--muted)">{row.patient.locationNote}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-(--muted)">—</span>
                  )}
                </td>
                <td className="p-3">
                  {row.errors.length > 0 ? (
                    <span className="text-red-600">Skipped — {row.errors.join('; ')}</span>
                  ) : row.warnings.length > 0 ? (
                    <span className="text-amber-700">Admitted — {row.warnings.join('; ')}</span>
                  ) : (
                    <span className="text-(--accent)">Admitted</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Count({ label, value, good, bad, warn }: {
  label: string; value: number; good?: boolean; bad?: boolean; warn?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-(--muted)">{label}</dt>
      <dd
        className={`text-2xl font-semibold tabular-nums ${
          bad ? 'text-red-600' : warn ? 'text-amber-700' : good ? 'text-(--accent)' : ''
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
